import { type ChangeEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { ActionSheet, Sheet } from "../../shared/phone/Sheet";
import { Chevron } from "../../shared/phone/Row";
import { Split } from "../../shared/phone/Split";
import { useRail } from "../../shared/phone/media";
import type { Asset, AssetFolder, PartnerAssetsView, PartnerOwnAsset } from "../../shared/types";
import { partnerApi } from "../lib/api";
import { extOf, ownAssetToAsset, ownCanPreview, typeLabel, useOwnAssets, usePreview } from "../lib/assets";
import { humanSize } from "../lib/format";
import { Icon } from "../components/phone/Icon";
import { EmptyBlock, PoweredBy } from "../components/phone/parts";
import { toast } from "../components/phone/Toast";

/** "Video, 18 MB", or a word count for swipe copy. */
function kindLine(a: Asset): string {
  if (a.type === "copy") {
    const words = (a.text ?? "").trim().split(/\s+/).filter(Boolean).length;
    return `Copy, ${words} ${words === 1 ? "word" : "words"}`;
  }
  return [typeLabel(a.type), humanSize(a.sizeBytes)].filter(Boolean).join(", ");
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast("Copied", "copy");
  } catch {
    toast("Copying is blocked here. Press and hold to copy.", "info");
  }
}

function download(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * A file's picture: an image once it has bytes, swipe copy as its words, anything else as its kind on a
 * tinted tile (a video with a play mark).
 */
function Thumb({ asset, url, size = "" }: { asset: Asset; url: string; size?: "" | "big" }) {
  const [broken, setBroken] = useState(false);
  const cls = ["po-th", size, asset.type].filter(Boolean).join(" ");
  if (asset.type === "copy") {
    return (
      <span className={cls} aria-hidden="true">
        <span>{asset.text}</span>
      </span>
    );
  }
  // Why: a file listed without a size has no bytes in storage yet, so asking for its picture only fails.
  if (asset.type === "image" && asset.sizeBytes !== null && !broken) {
    return (
      <span className={cls} aria-hidden="true">
        <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} />
      </span>
    );
  }
  return (
    <span className={cls} aria-hidden="true">
      <Icon name={asset.type === "image" ? "image" : asset.type === "video" ? "play" : "doc"} />
    </span>
  );
}

/** One file as a row: its picture, its name, its kind and size. Tap to preview it. */
function FileRow({ asset, url, onOpen, more }: { asset: Asset; url: string; onOpen: () => void; more?: ReactNode }) {
  const body = (
    <>
      <Thumb asset={asset} url={url} />
      <span className="m">
        <span className="t">{asset.name}</span>
        <span className="s">{kindLine(asset)}</span>
      </span>
    </>
  );
  if (!more) {
    return (
      <button type="button" className="pn-row po-f" onClick={onOpen}>
        {body}
      </button>
    );
  }
  return (
    <div className="pn-row po-f po-own">
      <button type="button" className="po-own-m" onClick={onOpen}>
        {body}
      </button>
      {more}
    </div>
  );
}

/** The preview of one file, with one big Download, or Copy text for swipe copy. */
function PreviewSheet({ asset, url, onClose }: { asset: Asset; url: string; onClose: () => void }) {
  const { url: blob, state, msg } = usePreview(asset, url);
  const isPdf = asset.contentType === "application/pdf" || extOf(asset) === "pdf";
  let shown: ReactNode;
  if (asset.type === "copy") shown = <div className="po-pv-copy">{asset.text}</div>;
  else if (state === "loading") shown = <div className="po-loading" role="status" aria-label="Loading" />;
  else if (state === "error") shown = <p className="po-pv-note">{msg} You can still download it to view.</p>;
  else if (blob && asset.type === "image") shown = <img className="po-pv-img" src={blob} alt={asset.name} />;
  else if (blob && asset.type === "video") shown = <video className="po-pv-img" src={blob} controls playsInline />;
  else if (blob && isPdf) shown = <iframe className="po-pv-frame" src={blob} title={asset.name} />;
  else shown = <Thumb asset={asset} url={url} size="big" />;

  return (
    <Sheet
      open
      onClose={onClose}
      title={asset.name}
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
      foot={
        asset.type === "copy" ? (
          <button type="button" className="pn-btn pri wide" onClick={() => void copyText(asset.text ?? "")}>
            <Icon name="copy" />
            Copy text
          </button>
        ) : (
          <a className="pn-btn pri wide" href={url} download>
            <Icon name="download" />
            Download
          </a>
        )
      }
    >
      <div className="po-pv">{shown}</div>
      <p className="po-pv-m">{kindLine(asset)}</p>
    </Sheet>
  );
}

const mine = (a: PartnerOwnAsset) => partnerApi.myAssetDownloadUrl(a.id);

interface Preview {
  asset: Asset;
  url: string;
}

/** A folder's files as rows, on its own screen on a phone and beside the list on an iPad. */
function FolderScreen({
  folder,
  files,
  productName,
  downloadUrl,
  onPreview,
  inPane,
}: {
  folder: AssetFolder | null;
  files: Asset[];
  productName: string;
  downloadUrl: (a: Asset) => string;
  onPreview: (p: Preview) => void;
  inPane: boolean;
}) {
  const back = inPane ? undefined : { to: "/assets", label: "Assets" };
  if (!folder) {
    return (
      <div className="pn-screen">
        <PhoneNav title="Assets" back={back} />
        <div className="pn-stack">
          <EmptyBlock icon="folder" title="Nothing here yet">
            Pick a folder to see what is in it.
          </EmptyBlock>
        </div>
      </div>
    );
  }
  return (
    <div className="pn-screen">
      <PhoneNav large title={folder.name} back={back} sub={`${files.length} ${files.length === 1 ? "item" : "items"} from ${productName}`} />
      <div className="pn-stack">
        {files.length ? (
          <div className="pn-grp">
            {files.map((a) => (
              <FileRow key={a.id} asset={a} url={downloadUrl(a)} onOpen={() => onPreview({ asset: a, url: downloadUrl(a) })} />
            ))}
          </div>
        ) : (
          <p className="po-none">No assets in this folder yet.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Assets on a phone and an iPad: a large title with search pinned under it, the owner's folders as rows,
 * then what the partner added. A folder opens as its own screen on a phone and beside the list on an iPad;
 * a file opens a preview sheet with Download.
 */
export function AssetsNative({
  view,
  error,
  onRetry,
  programId,
  folderId,
}: {
  view: PartnerAssetsView | null;
  error: string | null;
  onRetry: () => void;
  programId: string;
  folderId: string | null;
}) {
  const rail = useRail();
  const own = useOwnAssets(programId);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [menu, setMenu] = useState<PartnerOwnAsset | null>(null);
  const [confirm, setConfirm] = useState<PartnerOwnAsset | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const folders = view?.folders ?? [];
  const assets = view?.assets ?? [];
  const productName = view?.productName ?? "your program";
  const visible = useMemo(() => new Set(folders.map((f) => f.id)), [folders]);
  const rootFiles = assets.filter((a) => a.folderId === null || !visible.has(a.folderId));
  const shared = (a: Asset) => partnerApi.assetDownloadUrl(a.id, programId);
  const ownList = own.assets ?? [];

  const q = query.trim().toLowerCase();
  const hits = q
    ? [
        ...assets.map((a) => ({ asset: a, url: shared(a) })),
        ...ownList.map((a) => ({ asset: ownAssetToAsset(a), url: mine(a) })),
      ].filter(({ asset }) => `${asset.name} ${typeLabel(asset.type)}`.toLowerCase().includes(q))
    : [];

  // On an iPad the first folder shows beside the list until another is picked.
  const shownId = folderId ?? (rail ? (folders[0]?.id ?? null) : null);
  const folder = folders.find((f) => f.id === shownId) ?? null;

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && (await own.upload(file))) toast("Added");
  };

  let listBody: ReactNode;
  if (error && !view) {
    listBody = (
      <div className="pn-stack">
        <EmptyBlock icon="info" title="Something went wrong">
          {error}
        </EmptyBlock>
        <div className="pn-pad">
          <button type="button" className="pn-btn soft wide" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!view) {
    listBody = <div className="po-loading" role="status" aria-label="Loading" />;
  } else if (q) {
    listBody = (
      <div className="pn-stack po-as">
        {hits.length ? (
          <div className="pn-grp">
            {hits.map(({ asset, url }) => (
              <FileRow key={asset.id} asset={asset} url={url} onOpen={() => setPreview({ asset, url })} />
            ))}
          </div>
        ) : (
          <p className="po-none">Nothing matches.</p>
        )}
      </div>
    );
  } else {
    listBody = (
      <div className="pn-stack po-as">
        <section>
          <div className="pn-cap">Shared by {productName}</div>
          {folders.length || rootFiles.length ? (
            <div className="pn-grp">
              {folders.map((f) => (
                <Link key={f.id} to={`/assets/${f.id}`} className={f.id === shownId && rail ? "pn-row on" : "pn-row"}>
                  <span className="pn-ico">
                    <Icon name="folder" />
                  </span>
                  <span className="m">
                    <span className="t">{f.name}</span>
                    <span className="s">
                      {f.itemCount} {f.itemCount === 1 ? "item" : "items"}
                    </span>
                  </span>
                  <Chevron />
                </Link>
              ))}
              {rootFiles.map((a) => (
                <FileRow key={a.id} asset={a} url={shared(a)} onOpen={() => setPreview({ asset: a, url: shared(a) })} />
              ))}
            </div>
          ) : (
            <EmptyBlock icon="image" title="No assets shared yet">
              When {productName} shares videos, images or copy, they show up here.
            </EmptyBlock>
          )}
        </section>
        <section>
          <div className="pn-cap">Added by you</div>
          <div className="pn-grp">
            {ownList.map((a) => {
              const asAsset = ownAssetToAsset(a);
              return (
                <FileRow
                  key={a.id}
                  asset={asAsset}
                  url={mine(a)}
                  onOpen={() => (ownCanPreview(a) ? setPreview({ asset: asAsset, url: mine(a) }) : setMenu(a))}
                  more={
                    <button type="button" className="po-more" onClick={() => setMenu(a)} aria-label={`More for ${a.name}`}>
                      <Icon name="dots" />
                    </button>
                  }
                />
              );
            })}
            {own.uploading ? (
              <div className="pn-row static po-f">
                <span className="po-add-i">
                  <Icon name="plus" />
                </span>
                <span className="m">
                  <span className="t">Uploading {own.progress}%</span>
                  <span className="po-upb">
                    <i style={{ width: `${own.progress}%` }} />
                  </span>
                </span>
              </div>
            ) : (
              <button type="button" className="pn-row po-f accent" onClick={() => fileRef.current?.click()}>
                <span className="po-add-i">
                  <Icon name="plus" />
                </span>
                <span className="m">
                  <span className="t">Add your own</span>
                </span>
              </button>
            )}
          </div>
          {own.error ? (
            <p className="pn-note po-err" role="alert">
              {own.error}
            </p>
          ) : (
            <p className="pn-note">Only you can see these.</p>
          )}
          <input ref={fileRef} type="file" className="po-file" onChange={onPick} tabIndex={-1} aria-hidden="true" />
        </section>
        <PoweredBy />
      </div>
    );
  }

  const list = (
    <div className="pn-screen">
      <PhoneNav large title="Assets" />
      <div className="po-srch">
        <label className="pn-search">
          <Icon name="search" />
          <input type="search" placeholder="Search assets" aria-label="Search assets" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" enterKeyHint="search" />
        </label>
      </div>
      {listBody}
    </div>
  );

  const files = folder ? assets.filter((a) => a.folderId === folder.id) : [];
  const detail = <FolderScreen folder={folder} files={files} productName={productName} downloadUrl={shared} onPreview={setPreview} inPane={rail} />;

  return (
    <>
      <Split list={list} detail={detail} showDetail={!!folderId} />
      {preview ? <PreviewSheet asset={preview.asset} url={preview.url} onClose={() => setPreview(null)} /> : null}
      <ActionSheet
        open={!!menu}
        onClose={() => setMenu(null)}
        title={menu?.name}
        actions={
          menu
            ? [
                ...(ownCanPreview(menu) ? [{ label: "Preview", onSelect: () => setPreview({ asset: ownAssetToAsset(menu), url: mine(menu) }) }] : []),
                { label: "Download", onSelect: () => download(mine(menu)) },
                { label: "Delete", danger: true, onSelect: () => setConfirm(menu) },
              ]
            : []
        }
      />
      <ActionSheet
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? `Delete ${confirm.name}?` : undefined}
        message="This removes it for good."
        actions={
          confirm
            ? [
                {
                  label: "Delete",
                  danger: true,
                  onSelect: () => void own.remove(confirm.id).then((ok) => ok && toast("Deleted")),
                },
              ]
            : []
        }
      />
    </>
  );
}
